import React, { useState, useEffect } from 'react';
import { alpha } from '@mui/material/styles';
import { THEME_COLORS } from '../shared/constants/theme';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Drawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { detectThemeSync, watchThemeChanges } from '../utils/themeUtils';
import {
  getConfigurationActionHeaderRowSx,
  getConfigurationActionHeaderTitleSx,
  getConfigurationAddButtonSx,
  getConfigurationEmbeddedRootSx,
  isConfigurationMobileLayout,
} from '../utils/configurationMobileLayout';
import { getConfigurationRooms, createRoom, updateRoom, deleteRoom, getConfigurationRoomTypes, getConfigurationFloors } from '../services/api';
import ErrorState from '../components/common/ErrorState';

const STATUS_OPTIONS = [
  { value: 'available', label: 'Available', color: 'success' },
  { value: 'occupied', label: 'Occupied', color: 'error' },
  { value: 'maintenance', label: 'Maintenance', color: 'info' },
  { value: 'out_of_order', label: 'Out of Order', color: 'default' },
];

const CLEAN_STATUS_OPTIONS = [
  { value: 'clean', label: 'Clean', color: 'success' },
  { value: 'dirty', label: 'Dirty', color: 'warning' },
  { value: 'inspected', label: 'Inspected', color: 'info' },
];

const RoomPage = () => {
  const isMobileLayout = isConfigurationMobileLayout();
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const [roomsByFloor, setRoomsByFloor] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [floors, setFloors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roomToDelete, setRoomToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState({
    room_number: '',
    room_type_id: '',
    floor_id: '',
    status: 'available',
    clean_status: 'clean',
    notes: '',
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const cleanup = watchThemeChanges((newTheme) => {
      setCurrentTheme(newTheme);
    });
    return cleanup;
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  const isDark = currentTheme === 'dark';

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [roomsData, roomTypesData, floorsData] = await Promise.all([
        getConfigurationRooms(),
        getConfigurationRoomTypes(),
        getConfigurationFloors(),
      ]);
      setRoomsByFloor(roomsData);
      setRoomTypes(roomTypesData);
      setFloors(floorsData);
      setError(null);
    } catch (err) {setError(err);
      // Only show snackbar for non-auth errors
      if (err?.statusCode !== 401 && err?.statusCode !== 403) {
        setSnackbar({
          open: true,
          message: err?.userMessage || 'Failed to load data. Please try again.',
          severity: 'error',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDrawer = (room = null) => {
    if (room) {
      // Edit mode
      setEditingId(room.id);
      setFormData({
        room_number: room.room_number,
        room_type_id: room.room_type_id,
        floor_id: room.floor_id || '',
        status: room.status,
        clean_status: room.clean_status || 'clean',
        notes: room.notes || '',
      });
    } else {
      // Create mode
      setEditingId(null);
      setFormData({
        room_number: '',
        room_type_id: '',
        floor_id: '',
        status: 'available',
        clean_status: 'clean',
        notes: '',
      });
    }
    setErrors({});
    setOpenDrawer(true);
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    setEditingId(null);
    setFormData({
      room_number: '',
      room_type_id: '',
      floor_id: '',
      status: 'available',
      clean_status: 'clean',
      notes: '',
    });
    setErrors({});
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSaveRoom = async () => {
    setErrors({});
    setSaving(true);

    try {
      let response;
      const submitData = {
        ...formData,
        room_number: formData.room_number.trim() || '', // Empty string for auto-generation
        floor_id: formData.floor_id || null,
      };
      
      if (editingId) {
        // Update existing room
        response = await updateRoom(editingId, submitData);
        setSnackbar({
          open: true,
          message: response.message || 'Room updated successfully!',
          severity: 'success',
        });
      } else {
        // Create new room
        response = await createRoom(submitData);
        setSnackbar({
          open: true,
          message: response.message || 'Room created successfully!',
          severity: 'success',
        });
      }
      handleCloseDrawer();
      fetchData();
    } catch (error) {if (error.errors) {
        setErrors(error.errors);
      } else {
        setSnackbar({
          open: true,
          message: error.error || `Failed to ${editingId ? 'update' : 'create'} room. Please try again.`,
          severity: 'error',
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (room) => {
    setRoomToDelete(room);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!roomToDelete) return;

    setDeletingId(roomToDelete.id);
    try {
      await deleteRoom(roomToDelete.id);
      setSnackbar({
        open: true,
        message: 'Room deleted successfully!',
        severity: 'success',
      });
      setDeleteDialogOpen(false);
      setRoomToDelete(null);
      fetchData();
    } catch (error) {setSnackbar({
        open: true,
        message: error.error || 'Failed to delete room. Please try again.',
        severity: 'error',
      });
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setRoomToDelete(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getStatusColor = (status) => {
    const statusOption = STATUS_OPTIONS.find(opt => opt.value === status);
    return statusOption ? statusOption.color : 'default';
  };

  const getStatusLabel = (status) => {
    const statusOption = STATUS_OPTIONS.find(opt => opt.value === status);
    return statusOption ? statusOption.label : status;
  };

  const getCleanStatusColor = (cleanStatus) => {
    const cleanStatusOption = CLEAN_STATUS_OPTIONS.find(opt => opt.value === cleanStatus);
    return cleanStatusOption ? cleanStatusOption.color : 'default';
  };

  const getCleanStatusLabel = (cleanStatus) => {
    const cleanStatusOption = CLEAN_STATUS_OPTIONS.find(opt => opt.value === cleanStatus);
    return cleanStatusOption ? cleanStatusOption.label : cleanStatus;
  };

  return (
    <Box sx={getConfigurationEmbeddedRootSx(isMobileLayout)}>
      {/* Header */}
      <Box sx={getConfigurationActionHeaderRowSx(isMobileLayout)}>
        <Typography
          variant="h5"
          sx={{
            ...getConfigurationActionHeaderTitleSx(isMobileLayout),
            fontWeight: 700,
            color: isDark ? '#fff' : '#212121',
          }}
        >
          Rooms
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDrawer()}
          sx={getConfigurationAddButtonSx('#4ce1b6', isMobileLayout)}
        >
          Add Room
        </Button>
      </Box>

      {/* Loading State */}
      {loading && !error && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {!loading && error && (
        <ErrorState
          error={error}
          statusCode={error?.statusCode}
          onRetry={fetchData}
          showHomeButton={true}
        />
      )}

      {/* Rooms by Floor */}
      {!loading && !error && (
        <Box>
          {roomsByFloor.length === 0 ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              No rooms found. Click "Add Room" to create your first room.
            </Alert>
          ) : (
            roomsByFloor.map((floorGroup) => (
              <Accordion
                key={floorGroup.floor_id || 'no_floor'}
                defaultExpanded
                sx={{
                  mb: 2,
                  backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                  border: `1px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
                  borderRadius: 2,
                  '&:before': {
                    display: 'none',
                  },
                  boxShadow: 'none',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: '#4ce1b6' }} />}
                  sx={{
                    backgroundColor: 'action.hover',
                    borderRadius: 2,
                    '&.Mui-expanded': {
                      borderBottomLeftRadius: 0,
                      borderBottomRightRadius: 0,
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <MeetingRoomIcon sx={{ color: '#4ce1b6', fontSize: 28 }} />
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: isDark ? '#fff' : '#212121',
                        flexGrow: 1,
                      }}
                    >
                      {floorGroup.floor_display}
                    </Typography>
                    <Chip
                      label={`${floorGroup.rooms.length} Room${floorGroup.rooms.length !== 1 ? 's' : ''}`}
                      size="small"
                      sx={{
                        backgroundColor: '#4ce1b6',
                        color: isDark ? '#212121' : '#ffffff',
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 2 }}>
                  <Grid container spacing={1.5}>
                    {floorGroup.rooms.length === 0 ? (
                      <Grid item xs={12}>
                        <Alert severity="info">No rooms on this floor.</Alert>
                      </Grid>
                    ) : (
                      floorGroup.rooms.map((room) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={room.id}>
                          <Card
                            sx={{
                              height: '100%',
                              backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                              border: `1px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
                              borderRadius: 2,
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                boxShadow: isDark
                                  ? '0 8px 24px rgba(76, 225, 182, 0.2)'
                                  : '0 8px 24px rgba(25, 118, 210, 0.2)',
                                transform: 'translateY(-4px)',
                              },
                            }}
                          >
                            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                                <Typography
                                  variant="subtitle1"
                                  sx={{
                                    fontWeight: 700,
                                    color: isDark ? '#fff' : '#212121',
                                    fontSize: '0.95rem',
                                  }}
                                >
                                  {room.room_number}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.25 }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenDrawer(room)}
                                    disabled={deletingId === room.id}
                                    sx={{
                                      padding: '4px',
                                      color: '#4ce1b6',
                                      '&:hover': {
                                        backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(25, 118, 210, 0.1)',
                                      },
                                    }}
                                  >
                                    <EditIcon sx={{ fontSize: '16px' }} />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteClick(room)}
                                    disabled={deletingId === room.id}
                                    sx={{
                                      padding: '4px',
                                      color: isDark ? '#ff5252' : '#d32f2f',
                                      '&:hover': {
                                        backgroundColor: isDark ? 'rgba(255, 82, 82, 0.1)' : 'rgba(211, 47, 47, 0.1)',
                                      },
                                    }}
                                  >
                                    {deletingId === room.id ? (
                                      <CircularProgress size={16} />
                                    ) : (
                                      <DeleteIcon sx={{ fontSize: '16px' }} />
                                    )}
                                  </IconButton>
                                </Box>
                              </Box>

                              <Chip
                                label={room.room_type_name}
                                size="small"
                                sx={{
                                  mb: 0.5,
                                  height: '20px',
                                  fontSize: '0.7rem',
                                  backgroundColor: isDark ? alpha('#ffffff', 0.08) : '#e0e0e0',
                                  color: isDark ? '#ddd' : '#666',
                                  fontWeight: 600,
                                }}
                              />

                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                <Chip
                                  label={getStatusLabel(room.status)}
                                  size="small"
                                  color={getStatusColor(room.status)}
                                  sx={{
                                    height: '20px',
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                  }}
                                />
                                {room.clean_status && (
                                  <Chip
                                    label={getCleanStatusLabel(room.clean_status)}
                                    size="small"
                                    color={getCleanStatusColor(room.clean_status)}
                                    sx={{
                                      height: '20px',
                                      fontSize: '0.7rem',
                                      fontWeight: 600,
                                    }}
                                  />
                                )}
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))
                    )}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ))
          )}
        </Box>
      )}

      {/* Add/Edit Room Drawer — layout matches Room Type drawer */}
      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={handleCloseDrawer}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 600 },
            backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 1.5,
              borderBottom: `2px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
              backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MeetingRoomIcon sx={{ color: '#4ce1b6', fontSize: 24 }} />
              <Typography variant="h6" sx={{ color: isDark ? '#ddd' : '#333', fontWeight: 600 }}>
                {editingId ? 'Edit Room' : 'Add New Room'}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
              onClick={handleCloseDrawer}
              disabled={saving}
              sx={{
                borderColor: isDark ? 'rgba(76, 225, 182, 0.35)' : 'rgba(76, 225, 182, 0.45)',
                color: '#4ce1b6',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '999px',
                px: 1.5,
                py: 0.3,
                minWidth: 84,
                '&:hover': {
                  borderColor: '#4ce1b6',
                  backgroundColor: isDark ? 'rgba(76, 225, 182, 0.12)' : 'rgba(76, 225, 182, 0.08)',
                },
                '&.Mui-disabled': {
                  borderColor: isDark ? alpha('#ffffff', 0.08) : '#d0d0d0',
                  color: isDark ? '#666' : '#999',
                },
              }}
            >
              Close
            </Button>
          </Box>

          {/* Content */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 2.5,
            }}
          >
            <TextField
                label="Room Number"
                value={formData.room_number}
                onChange={(e) => handleInputChange('room_number', e.target.value)}
                fullWidth
                variant="outlined"
                error={!!errors.room_number}
                helperText={errors.room_number || 'Enter room number manually (e.g., 101, 201A). Leave empty for auto-generation.'}
                placeholder="e.g., 101, 201A, Suite-1"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                    '&:hover': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4ce1b6',
                      },
                    },
                    '&.Mui-focused': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4ce1b6',
                        borderWidth: '2px',
                      },
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#4ce1b6',
                  },
                }}
              />

            <TextField
                label="Room Type *"
                value={formData.room_type_id}
                onChange={(e) => handleInputChange('room_type_id', e.target.value)}
                fullWidth
                select
                variant="outlined"
                required
                error={!!errors.room_type_id}
                helperText={errors.room_type_id}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                    '&:hover': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4ce1b6',
                      },
                    },
                    '&.Mui-focused': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4ce1b6',
                        borderWidth: '2px',
                      },
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#4ce1b6',
                  },
                }}
              >
                {roomTypes.map((roomType) => (
                  <MenuItem key={roomType.id} value={roomType.id}>
                    {roomType.name} ({roomType.code})
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Floor"
                value={formData.floor_id}
                onChange={(e) => handleInputChange('floor_id', e.target.value)}
                fullWidth
                select
                variant="outlined"
                error={!!errors.floor_id}
                helperText={errors.floor_id || 'Optional: Select a floor for this room'}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                    '&:hover': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4ce1b6',
                      },
                    },
                    '&.Mui-focused': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4ce1b6',
                        borderWidth: '2px',
                      },
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#4ce1b6',
                  },
                }}
              >
                <MenuItem value="">No Floor</MenuItem>
                {floors.map((floor) => (
                  <MenuItem key={floor.id} value={floor.id}>
                    {floor.display_name || `Floor ${floor.floor_number}`}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Status *"
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                fullWidth
                select
                variant="outlined"
                required
                error={!!errors.status}
                helperText={errors.status}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                    '&:hover': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4ce1b6',
                      },
                    },
                    '&.Mui-focused': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4ce1b6',
                        borderWidth: '2px',
                      },
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#4ce1b6',
                  },
                }}
              >
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Clean Status *"
                value={formData.clean_status}
                onChange={(e) => handleInputChange('clean_status', e.target.value)}
                fullWidth
                select
                variant="outlined"
                required
                error={!!errors.clean_status}
                helperText={errors.clean_status}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                    '&:hover': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4ce1b6',
                      },
                    },
                    '&.Mui-focused': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4ce1b6',
                        borderWidth: '2px',
                      },
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#4ce1b6',
                  },
                }}
              >
                {CLEAN_STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Notes"
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                fullWidth
                variant="outlined"
                multiline
                rows={3}
                error={!!errors.notes}
                helperText={errors.notes}
                placeholder="Optional notes about the room..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                    '&:hover': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4ce1b6',
                      },
                    },
                    '&.Mui-focused': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4ce1b6',
                        borderWidth: '2px',
                      },
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#4ce1b6',
                  },
                }}
              />
          </Box>

          {/* Footer */}
          <Box
            sx={{
              p: 1.5,
              borderTop: `2px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
              backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
            }}
          >
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleCloseDrawer}
                  disabled={saving}
                  sx={{
                    borderColor: isDark ? alpha('#ffffff', 0.08) : '#e0e0e0',
                    color: isDark ? '#ddd' : '#666',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: '#4ce1b6',
                      backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                      color: '#4ce1b6',
                    },
                    '&:disabled': {
                      borderColor: isDark ? alpha('#ffffff', 0.08) : '#e0e0e0',
                      color: isDark ? '#666' : '#999',
                    },
                  }}
                >
                  Cancel
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleSaveRoom}
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : null}
                  sx={{
                    backgroundColor: '#4ce1b6',
                    color: '#000',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: '#4ce1b6',
                      opacity: 0.9,
                      boxShadow: `0 4px 12px ${isDark ? 'rgba(76, 225, 182, 0.4)' : 'rgba(76, 225, 182, 0.3)'}`,
                    },
                    '&:disabled': {
                      backgroundColor: isDark ? alpha('#ffffff', 0.08) : '#e0e0e0',
                      color: isDark ? '#999' : '#666',
                    },
                  }}
                >
                  {editingId ? 'Update Room' : 'Create Room'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Drawer>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        PaperProps={{
          sx: {
            backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
            border: `1px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
            minWidth: 400,
          },
        }}
      >
        <DialogTitle
          sx={{
            color: isDark ? '#fff' : '#212121',
            fontWeight: 600,
            pb: 2,
          }}
        >
          Delete Room
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              color: isDark ? '#ddd' : '#666',
              fontSize: '1rem',
            }}
          >
            Are you sure you want to delete room <strong>"{roomToDelete?.room_number}"</strong>?
            <br />
            <br />
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{
            p: 3,
            borderTop: `1px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
          }}
        >
          <Button
            onClick={handleDeleteCancel}
            sx={{
              color: isDark ? '#999' : '#757575',
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={deletingId !== null}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              '&:disabled': {
                backgroundColor: isDark ? '#555' : '#ccc',
              },
            }}
          >
            {deletingId !== null ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1, color: '#fff' }} />
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            width: '100%',
            backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
            color: isDark ? '#fff' : '#212121',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RoomPage;

